import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Package,
  Check,
  FileText,
  ListChecks,
  AlertTriangle,
  CheckCircle,
  PhoneCall,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCart } from "@/contexts/CartContext";
import { Template } from "@/types/templates";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";

const KitValueStack = () => {
  const [bundleProduct, setBundleProduct] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const { addBundleToCart, isInCart } = useCart();
  const navigate = useNavigate();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });

  useEffect(() => {
    const fetchBundle = async () => {
      try {
        const { data, error } = await supabase
          .from("products")
          .select("*")
          .eq("slug", "complete-package")
          .eq("is_active", true)
          .maybeSingle();

        if (error) throw error;
        if (data) setBundleProduct(data as Template);
      } catch (err) {
        console.error("Error fetching bundle:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBundle();
  }, []);

  const handlePurchase = () => {
    if (!bundleProduct) return;

    if (isInCart(bundleProduct.id)) {
      toast.info("Limit 1 per order for this kit.");
      navigate("/cart");
      return;
    }

    addBundleToCart(bundleProduct);
    toast.success("Complete DIY Kit added to cart!");
    navigate("/cart");
  };

  if (loading) {
    return (
      <section id="kit-section" className="py-12 lg:py-16 bg-muted/30">
        <div className="container-custom flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </section>
    );
  }

  if (!bundleProduct) return null;

  const valueItems = [
    { icon: FileText, label: "Template Pack (All Categories)", value: "$435" },
    { icon: ListChecks, label: "Dispute Flowchart (Step-by-step Map)", value: "$29" },
    { icon: AlertTriangle, label: 'Priority Checklist ("Dispute This First")', value: "$19" },
    { icon: CheckCircle, label: "Dispute Tracker (Rounds + Deadlines)", value: "$29" },
    { icon: PhoneCall, label: "Bureau Call Scripts (Word-for-word)", value: "$19" },
  ];

  const bonuses = [
    "My Credit Scrub DIY template guide",
    "10 Credit Mistakes to Avoid",
  ];

  const checklistItems = [
    "14 dispute letter templates",
    "Step-by-step process guide",
    "Dispute tracking spreadsheet",
    "Bureau contact scripts",
    "Priority checklist",
    "Bonus training materials",
  ];

  return (
    <section id="kit-section" className="py-12 lg:py-16 bg-muted/30 relative overflow-hidden">
      {/* Decorative sparkles */}
      <Sparkles className="absolute top-20 left-10 w-6 h-6 text-accent/40 animate-pulse-soft hidden lg:block" />
      <Sparkles className="absolute bottom-32 right-16 w-8 h-8 text-primary/30 animate-float hidden lg:block" />
      <Sparkles className="absolute top-40 right-1/4 w-5 h-5 text-accent/30 animate-pulse-soft animation-delay-400 hidden lg:block" />

      <div className="container-custom relative" ref={ref}>
        <div className={cn(
          "text-center mb-10 transition-all duration-700",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        )}>
          <Badge className="mb-4 bg-primary text-primary-foreground animate-pulse-soft">
            Most Popular
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Complete DIY <span className="text-gradient">Credit Scrub Kit</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Everything you need to dispute credit report errors yourself — the same
            process credit repair companies charge $1,500+ for.
          </p>
        </div>

        <Card className={cn(
          "max-w-4xl mx-auto border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-background to-accent/10 overflow-hidden transition-all duration-700",
          isVisible ? "opacity-100 translate-y-0 shadow-custom-lg" : "opacity-0 translate-y-8"
        )}>
          <div className="p-6 md:p-8">
            {/* Price and CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-8">
              <div className="text-center sm:text-left">
                <div className="flex items-baseline gap-3 justify-center sm:justify-start">
                  <span className="text-5xl font-bold text-primary number-highlight">$99</span>
                  <span className="text-xl text-muted-foreground line-through">
                    $530
                  </span>
                  <Badge variant="destructive" className="animate-pulse-soft">Save 81%</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  One-time payment • Instant download • Lifetime access
                </p>
              </div>

              <Button
                variant="gold"
                size="xl"
                className="gap-2 min-w-[200px] btn-glow-animated group"
                onClick={handlePurchase}
              >
                <Package className="w-5 h-5 group-hover:animate-bounce-subtle" />
                Get the Complete Kit
              </Button>
            </div>

            {/* Quick checklist with staggered animation */}
            <div className={cn(
              "grid sm:grid-cols-2 gap-3 mb-6 stagger-children",
              isVisible && "visible"
            )}>
              {checklistItems.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 group"
                >
                  <Check className={cn(
                    "w-5 h-5 text-primary shrink-0 transition-transform duration-300 group-hover:scale-110",
                    isVisible && `animate-check-in`
                  )}
                  style={{ animationDelay: isVisible ? `${idx * 100}ms` : '0ms' }}
                  />
                  <span className="text-foreground">{item}</span>
                </div>
              ))}
            </div>

            {/* Expandable value breakdown */}
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 text-primary font-medium hover:underline mx-auto group"
            >
              {expanded ? "Hide details" : "See what's included"}
              {expanded ? (
                <ChevronUp className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" />
              ) : (
                <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
              )}
            </button>

            <div className={cn(
              "overflow-hidden transition-all duration-500 ease-out",
              expanded ? "max-h-[600px] opacity-100 mt-6" : "max-h-0 opacity-0"
            )}>
              <div className="pt-6 border-t border-border/50">
                <div className="bg-background/50 rounded-xl p-5 border border-border/50">
                  <h4 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wide">
                    If Bought Separately (Estimated Value)
                  </h4>

                  <div className="space-y-3 mb-5">
                    {valueItems.map((item, idx) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between text-sm group"
                        >
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <Icon className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                            {item.label}
                          </span>
                          <span className="font-medium text-foreground">
                            {item.value}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-t border-border/50 pt-4 mb-4">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">
                      BONUSES INCLUDED:
                    </p>
                    <div className="space-y-2">
                      {bonuses.map((bonus, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 text-sm text-muted-foreground group"
                        >
                          <BookOpen className="w-4 h-4 text-primary group-hover:animate-wiggle" />
                          <span>Bonus #{idx + 1}: {bonus}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-border/50">
                    <span className="font-semibold text-foreground">
                      Total Estimated Value:
                    </span>
                    <span className="font-bold text-lg text-foreground">$530</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-semibold text-primary">Today's Price:</span>
                    <span className="font-bold text-2xl text-primary animate-pulse-soft">$99</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Helper text */}
            <p className="text-center text-sm text-muted-foreground mt-6">
              Not sure? Start here — most customers choose the complete kit.
            </p>
          </div>
        </Card>

        {/* Compliance note */}
        <p className={cn(
          "text-center text-xs text-muted-foreground mt-6 max-w-2xl mx-auto transition-all duration-700 delay-300",
          isVisible ? "opacity-100" : "opacity-0"
        )}>
          Educational tools only. Dispute only information you believe is
          inaccurate, incomplete, or unverifiable. Results vary by file and
          documentation.
        </p>
      </div>
    </section>
  );
};

export default KitValueStack;
